import { useTemplateHeaderData } from '@apps/admin/routes/WebsiteDesignerV2/routes/Template/hooks/useComingSoonDesignTemplates/useTemplateHeaderData';
import { ThemeVariationType } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { addError } from '@shared/utils/logger';
import type { SinglePhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { useToast } from '@withjoy/joykit';
import { useCallback } from 'react';
import { isWebsiteDesignSettingsFromQueryParamValid } from './util';

type WebsiteDesignColors = Readonly<{
  linkAndButton: string;
  artBackground: string;
  artFont: string;
  pageBackground: string;
  pageFont: string;
}>;

type WebsiteDesignSettings = Readonly<{
  readonly colors: WebsiteDesignColors;
  pageTransparency: boolean;
  pageShadow: boolean;
  photoPresentationLayout?: SinglePhotoPresentationLayout;
  fontPackName?: string;
  isBlankTemplate?: boolean;
}>;

/**
 * @returns {
 *   getWebsiteDesignSettings: a function that returns the website design settings from the query param
 *   getWebsiteHeaderLayout: a function that returns the template header data from the query param
 * }
 */
export const useWebsiteSettingsFromQueryParam = () => {
  const { wds: websiteDesignSettingsQueryParam, designThemeId, template } = useQueryParams();
  const { getTemplateHeaderData } = useTemplateHeaderData();

  const { tq } = useTranslation('createWedding');
  const { toast } = useToast();

  const getDesignSettings = useCallback(
    (encodedSettings: string | null): WebsiteDesignSettings | null => {
      try {
        if (!encodedSettings) {
          return null;
        }
        const decodedSettings: Record<string, unknown> = JSON.parse(Buffer.from(encodedSettings, 'base64').toString('utf-8'));
        if (!isWebsiteDesignSettingsFromQueryParamValid(decodedSettings)) {
          toast(tq`Invalid website design settings`);
          return null;
        }
        return decodedSettings as WebsiteDesignSettings;
      } catch (e) {
        toast(tq`Failed to parse website design settings`);
        addError(e, { message: 'CreateEvent: website design settings parsing error', encodedSettings });
        return null;
      }
    },
    [toast, tq],
  );

  const getWebsiteDesignSettings = useCallback(() => {
    const encodedSettings = typeof websiteDesignSettingsQueryParam === 'string' ? websiteDesignSettingsQueryParam : null;
    const decodedSettings = getDesignSettings(encodedSettings);
    if (decodedSettings) {
      const { colors, pageTransparency, pageShadow, photoPresentationLayout, fontPackName } = decodedSettings;
      return {
        colors: {
          linkAndButtonColor: colors?.linkAndButton,
          artBackgroundColor: colors?.artBackground,
          artFontColor: colors?.artFont,
          pageBackgroundColor: colors?.pageBackground,
          pageFontColor: colors?.pageFont,
        },
        pageTransparency,
        pageShadow,
        welcomePhotoPresentationLayout: photoPresentationLayout,
        fontPackName,
      };
    }
    return null;
  }, [getDesignSettings, websiteDesignSettingsQueryParam]);

  const getWebsiteHeaderLayout = useCallback(() => {
    if (designThemeId && template && Object.values(ThemeVariationType).includes(template as ThemeVariationType)) {
      const templateHeaderData = getTemplateHeaderData(designThemeId as string, template as ThemeVariationType);

      if (!templateHeaderData) {
        addError(`Template header data not found for design theme id: ${designThemeId} and variation type: ${template}`, { designThemeId, template });
        return null;
      }
      return templateHeaderData;
    } else {
      // if header layout settings are not present continue without header layout
      return null;
    }
  }, [getTemplateHeaderData, designThemeId, template]);

  return { getWebsiteDesignSettings, getWebsiteHeaderLayout };
};
